import Keycloak from "keycloak-js"
import type { KeycloakTokenParsed } from "keycloak-js"

const keycloakConfig = {
  url: process.env.NEXT_PUBLIC_KEYCLOAK_URL ?? "",
  realm: process.env.NEXT_PUBLIC_KEYCLOAK_REALM ?? "",
  clientId: process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID ?? "",
}

const TOKEN_REFRESH_INTERVAL_MS = 30_000
const TOKEN_MIN_VALIDITY_SECONDS = 60

let keycloak: Keycloak | null = null
let initPromise: Promise<boolean> | null = null
let refreshPromise: Promise<boolean> | null = null
let refreshIntervalId: number | null = null
const tokenChangeListeners = new Set<() => void>()

function assertBrowser() {
  if (typeof window === "undefined") {
    throw new Error("Keycloak can only be used in the browser")
  }
}

function assertConfig() {
  const missing = Object.entries(keycloakConfig)
    .filter(([, value]) => !value)
    .map(([key]) => key)

  if (missing.length > 0) {
    throw new Error(
      `Missing Keycloak config: ${missing.join(", ")}. Set NEXT_PUBLIC_KEYCLOAK_URL, NEXT_PUBLIC_KEYCLOAK_REALM, and NEXT_PUBLIC_KEYCLOAK_CLIENT_ID.`
    )
  }
}

function getKeycloak(): Keycloak {
  assertBrowser()
  assertConfig()

  if (!keycloak) {
    keycloak = new Keycloak(keycloakConfig)
  }

  return keycloak
}

function notifyTokenChanged() {
  tokenChangeListeners.forEach((listener) => listener())
}

function subscribeToTokenChanges(listener: () => void): () => void {
  tokenChangeListeners.add(listener)
  return () => {
    tokenChangeListeners.delete(listener)
  }
}

async function refreshToken(minValiditySeconds = TOKEN_MIN_VALIDITY_SECONDS): Promise<boolean> {
  const kc = getKeycloak()

  if (!refreshPromise) {
    refreshPromise = kc
      .updateToken(minValiditySeconds)
      .then((refreshed) => {
        if (refreshed) notifyTokenChanged()
        return refreshed
      })
      .catch((error) => {
        void kc.login()
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

function startTokenRefresh() {
  const kc = getKeycloak()

  kc.onTokenExpired = () => {
    void refreshToken(0).catch(() => {})
  }

  if (refreshIntervalId) return

  refreshIntervalId = window.setInterval(() => {
    void refreshToken().catch(() => {})
  }, TOKEN_REFRESH_INTERVAL_MS)
}

async function initKeycloak(): Promise<boolean> {
  const kc = getKeycloak()

  if (!initPromise) {
    initPromise = kc.init({
      onLoad: "login-required",
      pkceMethod: "S256",
    }).then((authenticated) => {
      if (authenticated) {
        startTokenRefresh()
        notifyTokenChanged()
      }
      return authenticated
    })
  }

  return initPromise
}

async function requireLogin(): Promise<void> {
  const authenticated = await initKeycloak()
  if (!authenticated) {
    await getKeycloak().login()
  }
}

async function getValidToken(): Promise<string | undefined> {
  await requireLogin()
  await refreshToken(30)
  return getKeycloak().token
}

function getToken(): string | undefined {
  return keycloak?.token
}

function isLoggedIn(): boolean {
  return Boolean(keycloak?.token)
}

function getTokenParsed(): KeycloakTokenParsed | undefined {
  return keycloak?.tokenParsed
}

function tokenClaims(): Record<string, unknown> | undefined {
  const parsed = keycloak?.tokenParsed
  return parsed as Record<string, unknown> | undefined
}

/** Prefer OIDC `name`; then given+family; then username; then email. */
function getDisplayName(): string {
  const p = tokenClaims()
  if (!p) return "User"
  if (typeof p.name === "string" && p.name.trim()) return p.name.trim()
  const given = typeof p.given_name === "string" ? p.given_name.trim() : ""
  const family = typeof p.family_name === "string" ? p.family_name.trim() : ""
  const combined = `${given} ${family}`.trim()
  if (combined) return combined
  if (typeof p.preferred_username === "string" && p.preferred_username.trim()) {
    return p.preferred_username.trim()
  }
  if (typeof p.email === "string" && p.email.trim()) return p.email.trim()
  return "User"
}

function getEmail(): string | undefined {
  const e = tokenClaims()?.email
  return typeof e === "string" && e.trim() ? e.trim() : undefined
}

/** Two-letter initials from a display name (e.g. JWT `name`). */
function initialsFromDisplayName(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return "?"
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    const a = parts[0][0]
    const b = parts[parts.length - 1][0]
    return `${a}${b}`.toUpperCase()
  }
  if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase()
  return parts[0][0]!.toUpperCase()
}

function getUserInitials(): string {
  return initialsFromDisplayName(getDisplayName())
}

function getUsername(): string | undefined {
  return keycloak?.tokenParsed?.preferred_username
}

function hasRealmRole(roles: string[]): boolean {
  return roles.some((role) => Boolean(keycloak?.hasRealmRole(role)))
}

function login(): Promise<void> {
  return getKeycloak().login()
}

function logout(): Promise<void> {
  return getKeycloak().logout({ redirectUri: window.location.origin })
}

export const UserService = {
  initKeycloak,
  requireLogin,
  login,
  logout,
  isLoggedIn,
  getToken,
  getValidToken,
  subscribeToTokenChanges,
  getTokenParsed,
  getDisplayName,
  getEmail,
  getUserInitials,
  initialsFromDisplayName,
  getUsername,
  hasRealmRole,
}
